import { ROOMS_STATUS_EVENT } from '@digital-twins/shared-types'
import type { RoomsStatusEvent } from '@digital-twins/shared-types'

import { connectWebSocket } from '../services/websocket'

/**
 * Room-health snapshot for the CURRENT connection, or `null` when unknown.
 * Cached at module scope because `rooms:status` is emitted once per connection:
 * a hook mounting after that emit would otherwise never learn the connection is
 * degraded. Cleared on disconnect so a stale "healthy" never outlives the
 * connection it described.
 *
 * Lives in its own module so repair hooks do not import a named export
 * declared late in `websocket.ts` (that throws under the HomeLab import graph).
 */
let lastRoomsStatus: RoomsStatusEvent | null = null
let disconnectBoundOn: ReturnType<typeof connectWebSocket> | null = null

function bindDisconnect(ws: ReturnType<typeof connectWebSocket>) {
  if (disconnectBoundOn === ws) {
    return
  }
  disconnectBoundOn = ws
  ws.on('disconnect', () => {
    lastRoomsStatus = null
  })
}

/**
 * Subscribe to room-health changes. Fires immediately with the cached status when
 * one is already known, so subscribers converge regardless of mount order.
 *
 * `degraded` means the server told us at least one class of event cannot reach
 * this socket (org-room join failed, or cross-pod delivery is down). Consumers
 * that removed their polling should widen their stall watchdog or revalidate on
 * focus while degraded rather than treating silence as "nothing changed".
 */
export function onRoomsStatus(
  callback: (status: RoomsStatusEvent) => void,
): () => void {
  const ws = connectWebSocket()
  bindDisconnect(ws)
  const handler = (status: RoomsStatusEvent) => {
    lastRoomsStatus = status
    callback(status)
  }
  ws.on(ROOMS_STATUS_EVENT, handler)
  if (lastRoomsStatus) {
    callback(lastRoomsStatus)
  }
  return () => ws.off(ROOMS_STATUS_EVENT, handler)
}
